/**
 * Date formatting utilities for conversation display
 */

/**
 * Format a date to a human-readable relative format
 * @param date - The date to format
 * @returns Formatted date string (e.g., "Now", "5 minutes ago", "Yesterday", etc.)
 */
export function formatRelativeDate(date: Date | string): string {
  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  
  // Calculate the difference in milliseconds
  const diffMs = now.getTime() - targetDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  // Handle future dates (should not happen, but just in case)
  if (diffMs < 0) {
    return 'Now';
  }
  
  // Less than 1 minute
  if (diffMinutes < 1) {
    return 'Now';
  }
  
  // Less than 1 hour
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
  }
  
  // Less than 24 hours
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  
  // Yesterday
  if (diffDays === 1) {
    return 'Yesterday';
  }
  
  // Less than 7 days
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }
  
  // Less than 30 days
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  
  // Less than 365 days
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }
  
  // More than 1 year
  const years = Math.floor(diffDays / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

/**
 * Format date for display in conversation list
 * @param date - The date to format
 * @returns Formatted date string optimized for sidebar display
 */
export function formatConversationDate(date: Date | string): string {
  return formatRelativeDate(date);
}
